import { useEffect, useState } from "react";
import { Button, Spin, Tabs, notification, Badge } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../axios";
import { StudentPersonalDetailsType, SubscriptionStatusType } from "../components/student-drawer/types";
import TabPane from "antd/es/tabs/TabPane";
import { PersonalDataDetailsPanel } from "../components/student-drawer/PersonalDataDetailsSection";
import { RequirementsTutorsListingPanel } from "../components/student-drawer/requirements-tutors/RequirementsTutorsPanel";
import { RequirementsDataDetailsPanel } from "../components/student-drawer/RequirementsDataDetailsPanel";
import TutorTabRender from "../components/student-drawer/TutorTabRender";
import { useAtom } from "jotai";
import { citysList } from "../states/citys";
import { SwapOutlined, IdcardOutlined } from "@ant-design/icons";
import TheSpinner from "../components/TheSpinner";
import { CommentsAndHistoryPanel } from "../components/student-drawer/personal-details/CommentsAndHistory";
import { StatusSwitch } from "../components/StatusSwitch";
import { STUDENT_DEALINGS_PRIORITY_LEVEL_COLORS, STUDENT_DEALINGS_STATUS_COLORS } from "../components/constants";
import { SubscriptionHistory } from "../components/student-drawer/personal-details/SubscriptionHistory";
import StudentPaymentDetail from "../components/student-drawer/requirements-tutors/StudentPaymentDetail";
import { CreRightSidePanel } from "../components/student-drawer/CreRightSidePanel";

import { ActionForNotification, NotificationType, popupTitleAndMessage } from "../components/utils/notificationPopup";
import { adminEditDealState, userRole } from "../states/userRole";
import { dateDisplayable } from "../components/table-utils";
import { USER_ROLES } from "../constants/roleConstants";
import CancelReasonTabRender from "../components/student-drawer/CancelReasonTabRender";
import { AttendanceTabPanel } from "../components/student-drawer/AttendanceTabPanel";

interface RouteParams extends Record<string, string | undefined> {
  dealings_id: string;
}

const StudentDetails: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [citys, _setCitys] = useAtom(citysList);
  const { dealings_id } = useParams<RouteParams>();
  const [spinning, setSpinning] = useState(false);
  const [NotificationInstanceApi, NotificationInstanceContextHolder] = notification.useNotification();
  const [studentDetails, setStudentDetails] = useState<StudentPersonalDetailsType | null>(null);
  const [studentDealStatus, setStudentDealStatus] = useState<keyof typeof STUDENT_DEALINGS_STATUS_COLORS | string>("");
  const [studentDealPriorityStatus, setStudentDealPriorityStatus] = useState<
    keyof typeof STUDENT_DEALINGS_PRIORITY_LEVEL_COLORS | string
  >("");
  const [customerType, setCustomerType] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusType>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_canAdminEditDeal, setCanAdminEditDeal] = useAtom(adminEditDealState);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [adminRole] = useAtom(userRole);
  const [cannotUpdateDealStatus, setCannotUpdateDealStatus] = useState(false);
  const [tentativeDate, setTentativeDate] = useState<string | null>(null);
  const [isRecall, setIsRecall] = useState(false);
  const [appliedTutorsCount, setAppliedTutorsCount] = useState(0);
  const [whatsappAppliedTutorsCount, setWhatsappAppliedTutorsCount] = useState(0);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchStudentDetails(dealings_id!);
    fetchAppliedTutorsCount(dealings_id!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealings_id]);

  useEffect(() => {
    console.log(
      'studentDealStatus === "ESCALATE" && adminRole === USER_ROLES.SUPERVISER',
      studentDealStatus === "ESCALATE" && adminRole === USER_ROLES.SUPERVISER,
    );
    setCannotUpdateDealStatus(studentDealStatus === "ESCALATE" && adminRole === USER_ROLES.SUPERVISER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentDealStatus]);

  useEffect(() => {
    fetchDealDetailStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentDetails]);


  async function fetchAppliedTutorsCount(dealings_id : any) {
    try {
      const { data } = await axiosInstance.get(`/v1/cre/student/recommendation-with-whatsapp-and-crm-applied-count`, {
        params: {
          dealings_id,
        },
      });
      setAppliedTutorsCount(data.data?.crm_applied_tutor_count || 0);
      setWhatsappAppliedTutorsCount(data.data?.whatsapp_applied_tutor_count || 0);
    } catch (error) {
      console.log(error);
    }
  }

  async function verifyStudentDetails(userId: string, studentDetails: any) {
    try {
      const { data } = await axiosInstance.post(`/v1/cre/user/verify-payment`, {
        userId: userId,
      });
      if (studentDetails.success) {
        setStudentDetails(studentDetails.data);
        setTentativeDate(studentDetails.data?.tentative_date);
        setStudentDealPriorityStatus(studentDetails.data?.priority_level ?? "P3");
        setCanAdminEditDeal(studentDetails?.data?.can_edit ?? false);
      } else {
        openNotificationWithIcon("error", "FETCH_STUDENT_DEAL", undefined, studentDetails.message);
        navigate("../404");
      }
    
    } catch (error) {
      console.log(error);
    }
  }

  async function fetchStudentDetails(dealings_id: string) {
    try {
      const { data } = await axiosInstance.get(`/v1/cre/student/detail`, {
        params: {
          dealings_id: dealings_id,
        },
      });
      verifyStudentDetails(data.data?.userId, data);
    } catch (error) {
      openNotificationWithIcon("error", "FETCH_STUDENT_DEAL");
    }
  }

  async function updateDealDetailStatus(newStatus: keyof typeof STUDENT_DEALINGS_STATUS_COLORS) {
    try {
      const { data } = await axiosInstance.post("/v1/cre/student/deal/status", {
        dealings_id: studentDetails?.id,
        deal_status: newStatus,
      });
      if (data.success) {
        setStudentDealStatus(data?.data?.status ?? newStatus);
        setTentativeDate(data.data?.tentative_date ?? tentativeDate);
        openNotificationWithIcon(
          "success",
          "UPDATE_DEAL_STATUS",
          newStatus,
          `Successfully updated Deal status to ${newStatus}`,
        );
      } else {
        openNotificationWithIcon("error", "UPDATE_DEAL_STATUS", newStatus, data.message);
      }
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "UPDATE_DEAL_STATUS",
        newStatus,
        `Faced an issue while updating Deal status to ${newStatus}`,
      );
    }
    setSpinning(false);
  }

  async function updateDealPriorityLevelStatus(newPriorityLevel: keyof typeof STUDENT_DEALINGS_PRIORITY_LEVEL_COLORS) {
    try {
      const { data } = await axiosInstance.post("/v1/cre/student/deal/status", {
        dealings_id: studentDetails?.id,
        priority_level: newPriorityLevel,
      });
      if (data.success) {
        setStudentDealPriorityStatus(data?.data?.priority_level ?? newPriorityLevel);
        openNotificationWithIcon(
          "success",
          "UPDATE_DEAL_PRIORITY_LEVEL",
          newPriorityLevel,
          `Successfully updated Deal Priority Level to ${newPriorityLevel}`,
        );
      } else {
        openNotificationWithIcon("error", "UPDATE_DEAL_PRIORITY_LEVEL", newPriorityLevel, data.message);
      }
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "UPDATE_DEAL_PRIORITY_LEVEL",
        newPriorityLevel,
        `Faced an issue while updating Deal Priority Level to ${newPriorityLevel}`,
      );
    }
    setSpinning(false);
  }

  async function fetchDealDetailStatus() {
    try {
      if (!studentDetails) {
      }
      const { data } = await axiosInstance.get(`/v1/cre/student/deal/status`, {
        params: {
          dealings_id: studentDetails?.id,
        },
      });
      setStudentDealStatus(data.data?.status);
      setStudentDealPriorityStatus(data.data?.priority_level ?? "P3");
      setCustomerType(data.data?.student_status);
    } catch (error) {}
  }

  const openNotificationWithIcon = (
    type: NotificationType,
    action: keyof typeof ActionForNotification,
    message?: string,
    description?: string,
  ) => {
    NotificationInstanceApi[type]({
      message: message ?? popupTitleAndMessage[action][type].title,
      description: description ?? popupTitleAndMessage[action][type].description,
      placement: "bottomRight",
    });
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <div className="tail-h-full">
        {NotificationInstanceContextHolder}
        {!studentDetails ? (
          <TheSpinner />
        ) : (
          <div className="tail-flex tail-flex-col lg:tail-flex-row tail-h-full tail-grow tail-items-stretch">
            <div className="tail-p-0 tail-grow tail-overflow-x-scroll">
              <div className="tail-flex tail-p-4 tail-justify-between tail-items-center">
                <div>
                  <h1 className="tail-text-xl tail-font-medium tail-mb-2">Id : {studentDetails.id}</h1>
                  <span className="tail-text-xs tail-text-gray-500">
                    Created: {dateDisplayable(studentDetails.created_at)}
                  </span>
                </div>
                <div>
                  <StatusSwitch
                    disabled={cannotUpdateDealStatus}
                    value={studentDealStatus}
                    options={STUDENT_DEALINGS_STATUS_COLORS}
                    onChange={(e) => {
                      setSpinning(true);
                      updateDealDetailStatus(e.target.value);
                    }}
                  ></StatusSwitch>
                </div>
                <div>
                  <Button
                    onClick={() => {
                      setSpinning(true);
                      updateDealDetailStatus("REPLACED");
                    }}
                    disabled={cannotUpdateDealStatus || studentDealStatus === "REPLACED"}
                    type="text"
                    icon={
                      <SwapOutlined className="tail-border tail-border-current tail-text-blue-500 tail-p-0.5 tail-rounded-md" />
                    }
                    iconPosition="end"
                  >
                    Replace{" "}
                  </Button>
                </div>
                <div>
                  <StatusSwitch
                    // disabled={!canAdminEditDeal}
                    value={studentDealPriorityStatus}
                    options={STUDENT_DEALINGS_PRIORITY_LEVEL_COLORS}
                    onChange={(e) => {
                      setSpinning(true);
                      updateDealPriorityLevelStatus(e.target.value);
                    }}
                  ></StatusSwitch>
                </div>
                <div>
                  Customer Type : <span className="tail-font-medium">{customerType} </span>
                </div>
              </div>

              <Tabs
                indicator={{ align: "start" }}
                defaultActiveKey="1"
                className="[&_.ant-tabs-nav]:tail-mb-0 [&_.ant-tabs-nav]:tail-shadow-none"
              >
                <TabPane tab="Personal Data" key="1" className="tail-p-4">
                  <PersonalDataDetailsPanel
                    setStudentDetails={setStudentDetails}
                    citys={citys}
                    studentDetails={studentDetails}
                    subscriptionStatus={subscriptionStatus}
                    setSubscriptionStatus={setSubscriptionStatus}
                  />
                  <CommentsAndHistoryPanel studentDetails={studentDetails} />
                  <SubscriptionHistory studentDetails={studentDetails} subscriptionStatus={subscriptionStatus} />
                </TabPane>
                <TabPane tab="Requirements" key="2" className="tail-p-4">
                  <RequirementsDataDetailsPanel
                    setStudentDetails={setStudentDetails}
                    studentDealStatus={studentDealStatus}
                    studentDetails={studentDetails}
                    citys={citys}
                    tentativeDate={tentativeDate}
                    setTentativeDate={setTentativeDate}
                  ></RequirementsDataDetailsPanel>
                  <RequirementsTutorsListingPanel
                    studentDetails={studentDetails}
                    setStudentDealStatus={setStudentDealStatus}
                  />
                  {/* <StudentRequirementTable dataSource={dataSource}
                handleCommentClick={handleCommentClick} isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} selectedRecord={selectedRecord} commentCounts={commentCounts} setCommentCounts={setCommentCounts} /> */}
                </TabPane>

                <TabPane tab="Payments" key="3" className="tail-p-4">
                  <StudentPaymentDetail studentDetails={studentDetails}></StudentPaymentDetail>
                </TabPane>

                <TabPane 
                  tab={
                    <span>
                      Tutors
                      <Badge 
                        count={whatsappAppliedTutorsCount} 
                        style={{ marginLeft: 4, backgroundColor: '#52c41a' }}
                        overflowCount={99}
                        title="WhatsApp Interest"
                      />
                      <Badge 
                        count={appliedTutorsCount} 
                        style={{ marginLeft: 4 }}
                        overflowCount={99}
                        title="CRM Applied"
                      />
                    </span>
                  } 
                  key="4"
                >
                  <TutorTabRender studentDetails={studentDetails}></TutorTabRender>
                </TabPane>
                <TabPane tab="Cancel Reason" key="5">
                  <CancelReasonTabRender studentDetails={studentDetails}></CancelReasonTabRender>
                </TabPane>
                <TabPane tab="Attendance" key="6" className="tail-p-4">
                  <AttendanceTabPanel dealings_id={dealings_id as string} />
                </TabPane>
              </Tabs>
            </div>
            <div className="tail-w-80 tail-min-w-80 tail-flex-col tail-flex tail-p-4 tail-gap-y-4">
              <CreRightSidePanel
                dealings_id={studentDetails.id}
                studentDealStatus={studentDealStatus}
                studentDealPriorityStatus={studentDealPriorityStatus}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StudentDetails;
